import React, { useState, useEffect, useCallback } from 'react'
import { Modal, Button, Form, InputGroup, ListGroup, Badge, Spinner, Alert } from '@themesberg/react-bootstrap'
import { useCookies } from 'react-cookie'

import { assignEmployee, getEmployeesForAssign } from '../../controller/api/device'
import { canCreateEmployees } from '../../helper/permission'
import AddNewEmployee from '../../pages/account/employee/sub-screen/add-new/AddNewEmployee'

const EmployeeItem = ({ employee, onSelect, isSelected, isAssigning }) => {
  return (
    <ListGroup.Item 
      action 
      onClick={() => onSelect(employee)}
      className={`d-flex justify-content-between align-items-center ${isSelected ? 'active' : ''}`}
      style={{ cursor: 'pointer' }}
    >
      <div>
        <div className="fw-bold">{employee.username}</div>
        <small className="text-muted">{employee.name || 'Không có tên'}</small>
        {employee.isConnected && employee.currentDevice && (
          <div>
            <Badge bg="info" className="mt-1">
              Đang kết nối: {employee.currentDevice.code}
            </Badge>
          </div>
        )}
      </div>
      {isAssigning && (
        <Spinner animation="border" size="sm" />
      )}
    </ListGroup.Item>
  )
}

export const EmployeeSelector = ({ 
  show, 
  onHide, 
  deviceId, 
  deviceCode, 
  currentEmployee,
  onAssignSuccess 
}) => {
  const [cookies] = useCookies(['lottmekong'])
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [assigning, setAssigning] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showCreateEmployeeModal, setShowCreateEmployeeModal] = useState(false)

  const loadEmployees = useCallback(async (searchTerm = '') => {
    setLoading(true)
    setError('')
    
    const result = await getEmployeesForAssign(cookies.lottmekong, searchTerm)
    
    if (result && result.status === 'success') {
      setEmployees(result.data || [])
    } else {
      setError('Không thể tải danh sách nhân viên')
    }
    
    setLoading(false)
  }, [cookies.lottmekong])

  useEffect(() => {
    if (show) {
      loadEmployees(search)
      setSelectedEmployee(null)
      setError('')
      setSuccess('')
    }
  }, [show, loadEmployees, search])

  const handleSearchChange = (e) => {
    setSearch(e.target.value)
  }

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee)
  }

  const handleAssign = async () => {
    if (!selectedEmployee) return

    setAssigning(true)
    setError('')
    setSuccess('')

    const result = await assignEmployee(cookies.lottmekong, deviceId, selectedEmployee.id)

    if (result && result.status === 'success') {
      setSuccess(`Đã gán ${selectedEmployee.username} cho ${deviceCode}`)
      setTimeout(() => {
        onAssignSuccess && onAssignSuccess(selectedEmployee)
        onHide()
      }, 1500)
    } else {
      setError(result?.message || 'Không thể gán nhân viên')
    }

    setAssigning(false)
  }

  const handleDisconnect = async () => {
    setAssigning(true)
    setError('')
    setSuccess('')

    const result = await assignEmployee(cookies.lottmekong, deviceId, null)

    if (result && result.status === 'success') {
      setSuccess(`Đã ngắt kết nối ${deviceCode}`)
      setTimeout(() => {
        onAssignSuccess && onAssignSuccess(null)
        onHide()
      }, 1500)
    } else {
      setError(result?.message || 'Không thể ngắt kết nối')
    }

    setAssigning(false)
  }

  const handleClearSearch = () => {
    setSearch('')
  }

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Gán nhân viên in vé cho {deviceCode}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-3">
            {success}
          </Alert>
        )}

        {currentEmployee && (
          <Alert variant="info" className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <span>Nhân viên in vé: <strong>{currentEmployee}</strong></span>
              <Button 
                variant="outline-danger" 
                size="sm" 
                onClick={handleDisconnect}
                disabled={assigning}
              >
                {assigning ? <Spinner animation="border" size="sm" /> : 'Ngắt kết nối'}
              </Button>
            </div>
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Tìm kiếm nhân viên in vé</Form.Label>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Nhập tên đăng nhập hoặc tên nhân viên in vé..."
              value={search}
              onChange={handleSearchChange}
            />
            {search && (
              <Button variant="outline-secondary" onClick={handleClearSearch}>
                ✕
              </Button>
            )}
          </InputGroup>
        </Form.Group>

        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">Danh sách nhân viên in vé ({employees.length})</h6>
            <div className="d-flex align-items-center">
              {canCreateEmployees() && (
                <Button
                  variant="success"
                  size="sm"
                  className="me-2"
                  onClick={() => setShowCreateEmployeeModal(true)}
                  disabled={loading || assigning}
                >
                  ➕ Tạo nhân viên
                </Button>
              )}
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={() => loadEmployees(search)}
                disabled={loading}
              >
                🔄 Làm mới
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <div className="mt-2">Đang tải...</div>
            </div>
          ) : employees.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <div className="mb-2">👥</div>
              <div>Không tìm thấy nhân viên in vé nào</div>
              {search && <small>Thử thay đổi từ khóa tìm kiếm</small>}
            </div>
          ) : (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <ListGroup>
                {employees.map(employee => (
                  <EmployeeItem
                    key={employee.id}
                    employee={employee}
                    onSelect={handleEmployeeSelect}
                    isSelected={selectedEmployee?.id === employee.id}
                    isAssigning={assigning && selectedEmployee?.id === employee.id}
                  />
                ))}
              </ListGroup>
            </div>
          )}
        </div>

        {selectedEmployee && (
          <Alert variant="light" className="mb-0">
            <strong>Đã chọn:</strong> {selectedEmployee.username}
            {selectedEmployee.isConnected && selectedEmployee.currentDevice && (
              <div className="mt-1">
                <small className="text-warning">
                  ⚠️ Nhân viên này đang kết nối với {selectedEmployee.currentDevice.code}
                </small>
              </div>
            )}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Hủy
        </Button>
        <Button 
          variant="primary" 
          onClick={handleAssign}
          disabled={!selectedEmployee || assigning}
        >
          {assigning ? <Spinner animation="border" size="sm" /> : 'Gán nhân viên in vé'}
        </Button>
      </Modal.Footer>
      </Modal>

      {canCreateEmployees() && (
        <AddNewEmployee
          show={showCreateEmployeeModal}
          onHide={() => setShowCreateEmployeeModal(false)}
          onSuccess={async () => {
            setSuccess('Tạo nhân viên thành công')
            await loadEmployees(search)
          }}
        />
      )}
    </>
  )
}

export default EmployeeSelector
